<template>
	<div class="module-header">
		<IconSelect
			v-model="module.icon"
			:disabled="readonly"
			:style="readonly ? 'pointer-events: none;' : ''"
			@update:model-value="handleIconChange" />
		<InputText
			ref="moduleNameInput"
			class="module-header__name-input"
			placeholder="Module Name"
			:class="{ 'module-header__name-input--readonly': readonly }"
			:readonly="readonly"
			:style="readonly ? 'pointer-events: none;' : ''"
			:value="isNameInputFocused ? moduleName : module.displayName"
			@blur="handleBlur"
			@focus="handleFocus"
			@input="handleInput"
			@keydown.enter="handleEnter"
			@keydown.esc="handleEsc" />
		<Button
			v-if="!readonly"
			class="module-header__delete-module-button"
			icon="fal fa-trash"
			label="Delete Module"
			outlined
			severity="danger"
			text
			@click="deleteModule()" />
	</div>
</template>

<script setup lang="ts">
import type { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import {
	DuplicateModuleError,
	EmptyModuleNameError,
	type EnrichedModule,
} from '@codecannon/schemas/schemas/EnrichedModule'
import IconSelect from '@/components/IconSelect.vue'
import { ref, watch, type ComponentPublicInstance } from 'vue'
import { useConfirm } from 'primevue'
import { anyToPascalCase } from '@codecannon/schemas/utils/strings'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'

const emit = defineEmits<{
	(e: 'name-updated', newName: string): void
	(e: 'icon-updated'): void
	(e: 'moduleRemoved'): void
}>()

const props = defineProps<{
	module: EnrichedModule
	app: EnrichedApp
	updating: boolean
	readonly: boolean
}>()

const confirm = useConfirm()
const moduleName = ref<string>('')
const moduleNameInput = ref<ComponentPublicInstance<typeof InputText> | null>(null)
const isNameInputFocused = ref<boolean>(false)

watch(
	() => props.module.name,
	() => {
		moduleName.value = props.module.name
	},
	{
		immediate: true,
	},
)

function handleInput(event: Event) {
	const target = event.target as HTMLInputElement
	moduleName.value = target.value
	clearValidationError()
}

function handleFocus() {
	if (!isNameInputFocused.value) {
		isNameInputFocused.value = true
		moduleName.value = props.module.name
	}
}

function handleBlur() {
	const validationFailed = !handleNameChange()
	if (validationFailed) {
		if (!moduleNameInput.value?.$el) {
			isNameInputFocused.value = false
			return
		}
		moduleNameInput.value.$el.focus()
		return
	}
	isNameInputFocused.value = false
}

function handleEnter() {
	if (!moduleNameInput.value?.$el) return
	moduleNameInput.value.$el.blur()
}

function handleEsc() {
	if (!moduleNameInput.value?.$el) return
	moduleName.value = props.module.name
	clearValidationError()
	moduleNameInput.value.$el.blur()
}

function clearValidationError() {
	if (!moduleNameInput.value?.$el) return
	moduleNameInput.value.$el.setCustomValidity('')
}

function handleIconChange() {
	emit('icon-updated')
}

function handleNameChange(): boolean {
	if (!moduleNameInput.value?.$el) return true

	moduleName.value = anyToPascalCase(moduleName.value)

	if (moduleName.value === props.module.name) {
		moduleNameInput.value.$el.blur()
		return true
	}

	try {
		props.module.setNames(moduleName.value)
		emit('name-updated', moduleName.value)
		moduleNameInput.value.$el.blur()
		return true
	} catch (error: unknown) {
		if (error instanceof DuplicateModuleError || error instanceof EmptyModuleNameError) {
			moduleNameInput.value.$el.setCustomValidity(error.message)
			moduleNameInput.value.$el.reportValidity()
			return false
		} else {
			throw error
		}
	}
}

function deleteModule() {
	confirm.require({
		message: `Are you sure you want to delete the ${props.module.name} module?`,
		header: 'Danger Zone',
		icon: 'fas fa-exclamation-triangle',
		rejectLabel: 'Cancel',
		rejectProps: {
			label: 'Cancel',
			severity: 'secondary',
			outlined: true,
		},
		acceptProps: {
			label: 'Delete',
			severity: 'danger',
		},
		accept: async () => {
			props.app.removeModule(props.module.name)
			emit('moduleRemoved')
		},
		reject: () => {},
	})
}
</script>

<style lang="scss" scoped>
.module-header {
	display: flex;
	flex: 1;
	align-items: center;
	gap: 8px;

	.module-header__name-input {
		flex: 1;
		font-size: 26px;
		font-weight: 600;
		background: transparent;
		border-color: transparent;
		padding: 4px 8px;
		box-shadow: none;

		&:not(:focus):not(.module-header__name-input--readonly):hover {
			border-color: var(--p-inputtext-hover-border-color);
			background: transparent;
		}

		&:focus {
			background: transparent;
			box-shadow: none;
		}

		&--readonly {
			cursor: default;
			opacity: 1;
		}
	}

	.module-header__delete-module-button {
		text-wrap: nowrap;
	}
}
</style>
